<template>
  <div class="boss-main-content-wrap">
    <setting-header
      v-if="isCompanyStaffAccess"
      :breadcrumbs="[{ name: '求人確認' },{ name: '掲載前の求人', pathName: 'jobPrePublished' }]"
      title="掲載前の求人プレビュー"
      class="hide-for-print"
    />

    <div
      data-test="usage-notes-and-print-button"
      class="grid-x hide-for-print"
    >
      <div class="cell auto pega-usage-notes">
        <mini-accordion
          v-if="hasWritePermission"
          wrap-class="pega-accordion-no-line"
          head-class="pega-accordion-link-header"
          title="利用方法・注意事項"
          @toggle="setLine"
          @toggled="setLine"
        >
          <ul class="pega-disc-list">
            <li>コメント・ハイライトモードONの状態で、文字を選択すると、その位置にハイライトとコメントが作成できます。</li>
            <li>企業様管理画面で作成したハイライトは青、営業・制作担当者は緑で表示されます。</li>
            <li>コメント・ハイライトモードONの状態で、ハイライト済みの文字をクリックすると、ハイライトを削除することができます。</li>
            <li>コメント・ハイライトモードをOFFにすることで、コメントを隠してレイアウトを確認することができます。</li>
            <li>ハイライト・残文字数表示・コメントは、掲載時には表示されません。</li>
            <li>お使いのブラウザによってはどのハイライトに対するコメントかを示す線が表示されない場合があります。推奨環境（Google Chrome）をご利用くださいますようお願いいたします。</li>
            <li><job-preview-provision-list-link /></li>
          </ul>
        </mini-accordion>
        <job-preview-provision-list-link v-else />
      </div>
      <print-button
        class="cell shrink"
        :is-simple="false"
        @print="print"
      />
    </div>

    <!-- ボタンエリア -->
    <div
      data-test="button-area"
      class="grid-x margin-bottom-1"
    >
      <!-- メッセージ位置までジャンプするボタン offsetはヘッダ分 -->
      <div class="cell hide-for-print">
        <button
          v-if="isFinishCreated && hasWritePermission"
          v-scroll-to="{ el:'#message', offset: -35}"
          class="boss-button-center boss-icon-message pega-preview-jump-button pega-red-button"
          data-test="message-jump"
        >
          {{ businessesPartnerName }}担当者とのメッセージはこちら
        </button>
      </div>
      <div class="cell hide-for-print">
        <sp-preview-button
          :id="Number(jobId)"
          :route-name="spRouteNameForJob"
        />
      </div>
      <div class="cell pega-text-small">
        ※「インタビュー」「フォトクリップ」は掲載時ご利用の場合のみ表示されます。
      </div>
    </div>

    <!-- コメント線 -->
    <template v-if="highlightMode">
      <job-preview-comment-line
        v-for="(rect,i) in rects"
        :key="i"
        :rect="rect"
      />
    </template>

    <!-- プレビューエリア -->
    <div
      v-if="isFinishCreated"
      :id="topParentHighlightId"
      :class="{'pega-show-highlight':highlightMode}"
      @select="selected"
      @click="selected($event)"
    >
      <!-- 求人情報 -->
      <job-preview
        :offer="offer"
        :comment-highlights="commentHighlights"
        :highlight-mode="highlightMode"
        :class="highlightMode ? 'pega-cursor-pen-only-id' : ''"
        @addComment="addComment"
        @changeComment="changeComment"
        @deleteComment="deleteComment"
        @deleteThread="deleteCommentThread"
        @onLoadImage="onLoadImageAction"
        @setLine="setLine"
        @toggleFixed="toggleFixed"
        @toggleThread="toggleThread"
      />

      <p class="pega-hide-msg">
        これ以降は、女の転職typeのユーザー画面では表示されません。ただし、提携サイトには表示される場合がございます。<br>
        あらかじめご了承ください。
      </p>
      <!-- マッチング情報 -->
      <job-matching
        :job-category-settings="offer.jobCategorySettings"
        :matching="matching"
        :comment-highlights="commentHighlights"
        :highlight-mode="highlightMode"
        :class="highlightMode ? 'pega-cursor-pen-only-id' : ''"
        @addComment="addComment"
        @changeComment="changeComment"
        @deleteComment="deleteComment"
        @deleteThread="deleteCommentThread"
        @setLine="setLine"
        @toggleFixed="toggleFixed"
        @toggleThread="toggleThread"
      />
    </div>

    <!-- メッセージ -->
    <job-pre-published-preview-message
      v-if="isFinishCreated && hasWritePermission"
      id="message"
      class="pega-table margin-bottom-3"
    />

    <!-- コメント確定ボタン -->
    <div
      v-if="hasWritePermission"
      class="pega-commit-comment hide-for-print"
      data-test="commit-comment"
    >
      <div>
        <span style="font-size: 0.9rem; color: white; vertical-align: super;">コメント・ハイライトモード</span>
        <span class="switch tiny">
          <input
            id="tinySwitch"
            v-model="highlightMode"
            class="switch-input"
            type="checkbox"
          >
          <label
            class="switch-paddle"
            for="tinySwitch"
          >
            <span
              class="switch-active"
              aria-hidden="true"
            >ON</span>
            <span
              class="switch-inactive"
              aria-hidden="true"
            >OFF</span>
          </label>
        </span>
      </div>
      <div>
        <button
          :disabled="!isExistsPendingComment"
          class="boss-button pega-button"
          data-test="comment-button"
          @click="updateComment"
        >
          <span class="boss-button-confirm-text">
            コメントを確定する
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import SettingHeader from '@/components/atoms/SettingHeader'
import MiniAccordion from '@/components/atoms/MiniAccordion'
import PrintButton from '@/components/atoms/PrintButton'
import SpPreviewButton from '@/components/atoms/SpPreviewButton'
import JobPreviewCommentLine from '@/components/atoms/JobPreviewCommentLine'
import JobPreview from '@/components/organisms/JobPreview'
import JobMatching from '@/components/organisms/JobMatching'
import JobPrePublishedPreviewMessage from '@/components/organisms/JobPrePublishedPreviewMessage'
import bossSCommon from '@/lib/bossSCommon'
import jobPreviewCreateHighlight from '@/lib/jobPreviewCreateHighlight'
import highlightUtil from '@/lib/highlightUtil'
import Indicator from '@/lib/indicator'
import { mapActions } from 'vuex'
import JobPreviewProvisionListLink from '@/components/atoms/JobPreviewProvisionListLink'

export default {
  components: {
    JobPreviewProvisionListLink,
    SettingHeader,
    MiniAccordion,
    PrintButton,
    SpPreviewButton,
    JobPreviewCommentLine,
    JobPreview,
    JobMatching,
    JobPrePublishedPreviewMessage
  },
  mixins: [bossSCommon, jobPreviewCreateHighlight],
  async beforeRouteLeave (_to, _from, next) {
    let leave = true
    if (this.isExistsPendingComment) {
      await this.$dialog.confirm('編集中のデータがあります。他のページに移動してよろしいですか？').catch(() => {
        leave = false
      })
    }
    leave ? next() : next(false)
  },
  data () {
    return {
      hasWritePermission: true,
      isFinishCreated: false, // createdの処理完了を待ってから描画するためのフラグ
      highlightMode: true,
      callOnLoadImageCount: 0, // イメージ読み込み完了が呼び出された回数
      isHighlighting: false, // ハイライト連打制御フラグ
      offer: {}, // 求人情報
      matching: {}, // マッチング設定
      commentHighlights: [], // コメントハイライト
      pendingComments: [], // 未確定コメント
      rects: [] // コメント線
    }
  },
  computed: {
    topParentHighlightId () { return highlightUtil.topParentHighlightId },
    businessesPartnerName () { return this.isCompanyStaffAccess ? '制作' : '企業' },
    isExistsPendingComment () {
      // 更新（=idあり） または コメント1文字以上のものがない場合は非活性
      return this.pendingComments.filter(c => c.id || c.comment.trim()).length
    },
    // 読み込む画像の枚数カウント
    imageCount () {
      let imageCount = 0
      if (this.offer.description.mainPhoto) imageCount++
      if (this.offer.requirements.freeTopics[0]?.photo) imageCount++
      if (this.offer.requirements.freeTopics[1]?.photo) imageCount++
      if (!this.offer.description.isLightPlan) { // ライトプラン以外でのみ表示
        if (this.offer.companyInfo?.introImage) imageCount++
        if (this.offer.companyInfo?.companyImage1Caption && this.offer.companyInfo.companyImage1) imageCount++
        if (this.offer.companyInfo?.companyImage2Caption && this.offer.companyInfo.companyImage2) imageCount++
        if (this.offer.companyInfo?.companyImage3Caption && this.offer.companyInfo.companyImage3) imageCount++
      }
      return imageCount
    },
    afterPrint () { return () => { this.changePrintStatus(false) } }
  },
  watch: {
    highlightMode () {
      // false → trueにされた時、ハイライト線を再描画する
      this.highlightMode && this.setLine()
    }
  },
  async created () {
    await this.setShowCommentsAndMessageStatus()
    this.callApis()
  },
  mounted () {
    // 印刷画面close時に画面上の表示を切り替える
    window.addEventListener('afterprint', this.afterPrint)
  },
  beforeDestroy () {
    // eventListenerの削除
    window.removeEventListener('afterprint', this.afterPrint)
  },
  methods: {
    ...mapActions([
      'storeMaster'
    ]),

    // create:書き込み権限によるコメントハイライト・メッセージ表示切り替え
    async setShowCommentsAndMessageStatus () {
    // コメント情報の取得・表示切り替え
      if (!this.isCompanyStaffAccess) {
        if (this.$route.name === 'bossSPrePublishedConfirmJobPreview') { // BOSS-S確定職種
          // 確定職種プレビュー = 書き込み権限なし
          this.$set(this, 'hasWritePermission', false)
          this.$set(this, 'highlightMode', false)
        } else {
          // 書き込み権限確認API
          await this.$http.secured.get(`${this.apiPath}/has_write_permission`).then(response => {
            this.$set(this, 'hasWritePermission', response.data)
            this.$set(this, 'highlightMode', response.data)
          })
        }
      }
    },

    // create:API呼び出し
    callApis () {
      const apis = [
        // 求人情報取得API
        this.$http.secured.get(this.apiPathForFetchJobs),
        // マッチング設定取得API
        this.$http.secured.get(`${this.apiPath}/matching`)
      ]
      // コメントハイライトスレッド一覧取得API
      if (this.hasWritePermission) { apis.push(this.$http.secured.get(`${this.apiPath}/threads`)) }

      // マスタ一括取得
      apis.push(
        this.storeMaster('prIcons'),
        this.storeMaster('skills')
      )

      Promise.all(apis).then(([
        offerResponse, // 求人情報取得API
        matchingResponse, // マッチング設定取得API
        threadResponse // コメントハイライトスレッド一覧取得API
      ]) => {
        this.$set(this, 'offer', offerResponse.data)
        this.$set(this, 'matching', matchingResponse.data || null)
        if (this.hasWritePermission) { this.$set(this, 'commentHighlights', threadResponse.data) }

        this.$set(this, 'isFinishCreated', true)

        if (!this.imageCount) {
          this.onLoadImageAction() // 画像の読み込みがないため、画像読み込み後処理を呼んでおく
        }
      })
    },

    // 画像読み込み後処理
    async onLoadImageAction () {
      this.callOnLoadImageCount++
      if (this.imageCount && this.callOnLoadImageCount !== this.imageCount) return // まだ全ての画像がロードされていないため処理を省略

      // 画面描画
      await this.$nextTick()

      // 座標の記録
      this.setOffsetPositionForCommentHighlights()
      this.sortHighlightData()
    },

    // ハイライト一覧全てにハイライト位置座標を記録
    setOffsetPositionForCommentHighlights () {
      _.forEach(this.commentHighlights, async (commentHighlight) => {
        if (!commentHighlight.highlight) return // ハイライト情報がない場合は描画不要
        const highlightElement = document.getElementById(highlightUtil.createHighlightId(commentHighlight.target, commentHighlight.highlight.start))
        if (!highlightElement) return // 画面上からハイライト情報が取得できない場合は描画不要

        // ハイライト座標をDataに記録
        highlightUtil.setOffsetPosition(commentHighlight, highlightElement)
      })
    },

    // 選択時処理
    async selected (e = null) {
      // ハイライトモードではない場合or別のハイライト処理が実行中の場合は動作しない
      if (!this.highlightMode || this.isHighlighting) return

      // 連打制御フラグ
      this.$set(this, 'isHighlighting', true)

      await this.selectedAction(e)

      // 連打制御フラグを戻す
      this.$set(this, 'isHighlighting', false)
    },

    // コメント確定
    updateComment () {
      Indicator.emitShow()

      // 更新（=idあり） または コメント1文字以上のみAPIに渡す
      // - 更新 かつ コメント0文字はAPI側で削除に倒す
      const comments = this.pendingComments.filter(pendingComment => {
        return pendingComment.id || pendingComment.comment.trim()
      })

      // コメント登録・更新API呼び出し
      this.$http.secured.put(`${this.apiPath}/threads/comments`, comments)
        .then(async response => {
          // dataのリセット
          this.$set(this, 'pendingComments', [])
          await this.$nextTick()

          // レスポンスからスレッド一覧を再設定
          this.$set(this, 'commentHighlights', response.data)
          this.setOffsetPositionForCommentHighlights()
          this.sortHighlightData()

          this.$toasted.global.success({ message: 'コメントを更新しました。' })
        }).finally(_ => {
          Indicator.emitHide()
        })
    },

    toggleFixed (comments, isFixed) {
      const commentIds = comments.map(comment => comment.id)

      // 修正ステータス更新API
      this.$http.secured.put(`${this.apiPath}/threads/comments/fixed`, { commentIds: commentIds, isFixed: isFixed })

      const targetThreadId = comments[0].threadId // 必ず同一のスレッドのものが渡ってくるので1件目を見る
      // コメント修正済み切り替え
      this.commentHighlights
        .find(thread => thread.id === targetThreadId)
        .comments
        .filter(comment => commentIds.includes(comment.id))
        .forEach(comment => { this.$set(comment, 'isFixed', isFixed) })

      this.$toasted.global.success({ message: isFixed ? 'コメントを「修正済み」にしました。' : '「修正済み」を解除しました。' })
    },

    // コメント開閉切り替え
    toggleThread (thread) {
      // コメント開閉状態更新API
      this.$http.secured.put(`${this.apiPath}/threads/${thread.id}/open`, { isOpen: !thread.isOpen })

      // データの書き換え
      this.$set(thread, 'isOpen', !thread.isOpen)

      // コメント線の描画
      this.setLine()
    },

    // 変更されたコメントの記録
    changeComment (comment) {
      this.$set(comment, 'isChanged', true)
      this.pendingComments.push(comment)
    },

    deleteComment (comment) {
      // コメント削除API
      this.$http.secured.delete(`${this.apiPath}/threads/${comment.threadId}/comments/${comment.id}`)
      // コメントデータ削除
      const targetThread = this.commentHighlights.find(thread => thread.id === comment.threadId)
      this.$set(targetThread, 'comments', targetThread.comments.filter((c) => c.id !== comment.id))
      // 更新リストに未確定コメントがあった場合も削除
      this.$set(this, 'pendingComments', this.pendingComments.filter((c) => c.id !== comment.id))
      // コメント線の描画
      this.setLine()
      this.$toasted.global.success({ message: 'コメントを削除しました。' })
    },

    // コメントハイライトスレッド削除（コメントスレッドの削除ボタンから呼び出し）
    async deleteCommentThread (thread) {
      // スレッド削除可否チェック
      if (!this.canDeleteThread(thread)) return

      this.deleteThread(thread).then(isDelete => {
        if (isDelete) {
          highlightUtil.deleteHighlight(thread)
        }
      })
    },

    // スレッド削除
    async deleteThread (thread) {
      let isDelete
      await this.$dialog.confirm('このハイライトとスレッドを削除しますか？')
        // はいを押下した時
        .then(() => {
          isDelete = this.deleteThreadAction(thread)
        })
        // いいえを押下した時（catch句を消すとコンソールに警告が出るから消さないでね！！）
        .catch(() => {
          isDelete = false
        })
      return isDelete
    },

    deleteThreadAction (thread) {
      // スレッド削除API
      this.$http.secured.delete(`${this.apiPath}/threads/${thread.id}`)
      // スレッドデータ削除
      this.$set(this, 'commentHighlights', this.commentHighlights.filter((ch) => ch.id !== thread.id))
      // 更新リストに未確定コメントがあった場合も削除
      this.$set(this, 'pendingComments', this.pendingComments.filter((c) => c.threadId !== thread.id))
      // コメント線の描画
      this.setLine()
      this.$toasted.global.success({ message: 'ハイライトとスレッドを削除しました。' })
      return true
    },

    // コメント線の描画
    async setLine () {
      this.$set(this, 'rects', [])
      await this.$nextTick() // PreviewJobの描画分の待機
      _.forEach(this.commentHighlights, async (commentHighlight) => {
        if (!commentHighlight.highlight) return // ハイライト情報がない場合は描画不要
        await this.$nextTick()
        const highlight = document.getElementById(highlightUtil.createHighlightId(commentHighlight.target, commentHighlight.highlight.start))
        const comment = document.getElementById(`comment-${commentHighlight.target}-${commentHighlight.highlight.start}`)
        if (!highlight || !comment) return // ハイライト・コメントが取得できない場合は描画しない

        const highlightRect = highlight.getBoundingClientRect()
        const commentRect = comment.getBoundingClientRect()
        this.rects.push({
          id: commentHighlight.id,
          x1: highlightRect.right,
          y1: highlightRect.top,
          x2: commentRect.left,
          y2: commentRect.top + 10 // マージン
        })
      })
    },

    // コメントハイライト情報をハイライト位置でソート
    async sortHighlightData () {
      await this.$nextTick()
      // ハイライトのソート
      this.commentHighlights.sort((a, b) => {
        // ハイライトがないものは末尾
        if (!a.highlight || a.parentTop === undefined) return 1
        if (!b.highlight || b.parentTop === undefined) return -1
        // ハイライト位置でソート
        return a.parentTop - b.parentTop ||
        a.top - b.top ||
        a.left - b.left ||
        a.highlight.start - b.highlight.start ||
        0
      })
      // コメント線の描画
      this.setLine()
    },

    // コメントハイライト取得
    getCommentHighlightOnlyOne (target) {
      // エリアハイライト（ターゲット名：コメント ＝ １：１）のみ利用可能
      return this.commentHighlights.find(i => i.highlight && target.indexOf(i.target) > -1)
    },

    // 印刷
    async print () {
      await this.changePrintStatus(true)
      window.print()
    },

    // 印刷ステータス切り替え
    async changePrintStatus (status) {
      // 印刷用にハイライト・コメント表示を切り替える（印刷時非表示）
      this.$set(this, 'highlightMode', !status)
      await this.$nextTick()
    }
  }
}
</script>
